import React from "react";
import {
  CalendarDays,
  List,
  MapPin,
  MessageCircle,
  MousePointerClick,
  Search,
  Megaphone,
  SlidersHorizontal,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/atoms";
import type { PublicPageGuideStep, PublicPageGuideStepIcon } from "@/types";
import { Modal } from "../Modal";
import "./PublicPageGuideModal.scss";

const STEP_ICONS: Record<PublicPageGuideStepIcon, LucideIcon> = {
  search: Search,
  mapPin: MapPin,
  message: MessageCircle,
  list: List,
  filter: SlidersHorizontal,
  send: MousePointerClick,
  calendar: CalendarDays,
  megaphone: Megaphone,
};

export interface PublicPageGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  intro: string;
  steps: readonly PublicPageGuideStep[];
  confirmLabel: string;
  actionTrackSufix: string;
}

const GuideStepsList: React.FC<{
  steps: readonly PublicPageGuideStep[];
  layoutClass?: string;
  stepClass?: string;
}> = ({ steps, layoutClass = "", stepClass = "" }) => (
  <div className={`page-guide-modal__steps ${layoutClass}`.trim()}>
    {steps.map((step) => {
      const Icon = STEP_ICONS[step.icon];

      return (
        <article
          key={step.title}
          className={`page-guide-modal__step ${stepClass}`.trim()}
        >
          <div className="page-guide-modal__step-icon-wrap">
            <Icon className="page-guide-modal__step-icon" aria-hidden />
          </div>
          <div className="page-guide-modal__step-copy">
            <h3 className="page-guide-modal__step-title">{step.title}</h3>
            <p className="page-guide-modal__step-body">{step.body}</p>
          </div>
        </article>
      );
    })}
  </div>
);

/**
 * First-visit onboarding modal for public pages (salas, castings, calendario).
 */
export const PublicPageGuideModal: React.FC<PublicPageGuideModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  intro,
  steps,
  confirmLabel,
  actionTrackSufix,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="page-guide-modal page-guide-modal--spotlight">
        <p className="page-guide-modal__intro page-guide-modal__intro--spotlight">
          {intro}
        </p>
        <GuideStepsList
          steps={steps}
          layoutClass="page-guide-modal__steps--accent"
        />
        <Button
          type="button"
          variant="primary"
          appearance="solid"
          fullWidth
          density="default"
          motionScale="lg"
          actionTrackSufix={actionTrackSufix}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default PublicPageGuideModal;
